import { computed, createModel } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { isParameterTyped, simplifyType } from '@thinkalpha/language-services';
import type { FunctionDef } from '@thinkalpha/language-services';
import type { OutputColumnWithMetadata } from '@thinkalpha/platform-ws-client/contracts/table.js';
import { smartFilterToTableFilter } from 'src/components/smart-filter';

function tableColumnFunctionDefs(columns: readonly OutputColumnWithMetadata[]): FunctionDef[] {
    return columns.flatMap((column) => {
        if (isParameterTyped(column.dataType)) return [];
        return [
            {
                id: column.id,
                symbol: column.id,
                overloads: [{ params: [], returnType: simplifyType(column.dataType) }],
            },
        ];
    });
}

export function compileTableV2SmartFilter(
    filter: string,
    columns: readonly OutputColumnWithMetadata[],
): string | undefined {
    return smartFilterToTableFilter(filter, tableColumnFunctionDefs(columns)) || undefined;
}

export function combineTableV2Filters(...filters: readonly (string | undefined)[]): string | undefined {
    return (
        filters
            .filter((filter): filter is string => Boolean(filter))
            .map((filter) => `(${filter})`)
            .join(' and ') || undefined
    );
}

export const TableV2FilterModel = createModel(
    (init: {
        columns: ReadonlySignal<OutputColumnWithMetadata[]>;
        linkingFilter: ReadonlySignal<string | undefined>;
        stickyFilter: ReadonlySignal<string>;
    }) => {
        const smartFilterFormula = computed(() =>
            compileTableV2SmartFilter(init.stickyFilter.value, init.columns.value),
        );
        const effectiveFormula = computed(() =>
            combineTableV2Filters(init.linkingFilter.value, smartFilterFormula.value),
        );

        return { effectiveFormula, smartFilterFormula };
    },
);

export type TableV2FilterModel = InstanceType<typeof TableV2FilterModel>;
